"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  GitCompareArrows,
  History,
  Split,
} from "lucide-react";
import {
  StatusBadge,
  buildDataTableThemeVars,
} from "@aera/data-table";
import {
  AeraButton,
  Modal,
  ModalCloseButton,
  ModalHeader,
  ModalShell,
  SegmentedTabSwitcher,
  type SegmentedTabItem,
} from "@aera/ui";
import { ReceivableDetailsTab } from "@/app/artifacts/table/ReceivableDetailsTab";
import { ReceivableHistoryTab } from "@/app/artifacts/table/ReceivableHistoryTab";
import { ReceivableCorrectionsPanel } from "@/app/artifacts/table/ReceivableCorrectionsPanel";
import type {
  PendingAddressDraft,
} from "@/app/artifacts/table/ReceivablePendingAddressEditor";
import {
  isPendingAddressDraftDirty,
  isPendingAddressDraftValid,
  pickPendingAddressDraft,
  savePendingAddressDraft,
} from "@/app/artifacts/table/ReceivablePendingAddressEditor";
import { ReceivableStaleBanner, ReceivableViewingStaleChip } from "@/app/artifacts/table/ReceivableStaleBanner";
import {
  buildReceivableStaleConflict,
  cloneReceivableListItem,
  detectStaleReceivableEdit,
  enrichStaleNoticeFromHistory,
  fingerprintReceivableItem,
  markOwnReceivableEditInThisTab,
  wasOwnReceivableEditInThisTab,
  type ReceivableStaleConflict,
  type ReceivableStaleNotice,
} from "@/app/artifacts/table/receivableStale";
import {
  fetchPendingItemHistory,
  pickLatestHistoryMeta,
} from "@/app/artifacts/table/receivableHistory";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import { resolveReceivableDisplayStatus, isReceivableArchivedDisplayStatus, filterReceivableMoneyCandidates } from "@/lib/receivableDisplayStatus";
import {
  fetchReceivableDebtsDetails,
  formatReceivableExpiryRemaining,
  getReceivableOrderId,
  isReceivableAddressItem,
  isReceivableAnnouncementItem,
  isReceivableSlipItem,
  pickReceivableSlipImageUrl,
  resolveOwnerUserId,
} from "@/lib/pendingOrders";
import {
  ReceivableMatchPaymentModal,
  type ReceivableMatchModalMode,
} from "@/app/artifacts/table/ReceivableMatchPaymentModal";
import { CreateReceivableFromSlipModal } from "@/app/artifacts/table/CreateReceivableFromSlipModal";
import {
  ResplitAllocationModal,
  ResplitAllocationSkeleton,
} from "@/app/artifacts/table/ReceivableMoveResplitModals";
import { offerMoneyUndoToast, pickMoneyUndoSourceLabel } from "@/lib/moneyUndoToast";
import {
  canUnmatchReceivable,
  canDismissPendingItem,
  canEditPendingAddress,
  getDismissPendingBlockReason,
  isDismissPendingActionVisible,
  fetchPaymentAllocations,
  fetchUnmatchCandidates,
} from "@/lib/receivableCorrections";
import { postDismissPending } from "@/lib/moneyOperations";
import { pickReceivableId } from "@/app/artifacts/table/receivableHistory";
import { showToast } from "@/app/components/ui/Toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";

export { ReceivableFieldCell } from "@/app/artifacts/table/ReceivableFieldCell";

type ReceivableDrawerTab = "details" | "corrections" | "resplit" | "history";

const RECEIVABLE_DRAWER_TAB_DETAILS: SegmentedTabItem<ReceivableDrawerTab> = {
  id: "details",
  label: "ข้อมูล",
  icon: FileText,
};
const RECEIVABLE_DRAWER_TAB_RESPLIT: SegmentedTabItem<ReceivableDrawerTab> = {
  id: "resplit",
  label: "ยอดจัดสรร",
  icon: Split,
};
const RECEIVABLE_DRAWER_TAB_CORRECTIONS: SegmentedTabItem<ReceivableDrawerTab> = {
  id: "corrections",
  label: "แก้ไขจับคู่",
  icon: GitCompareArrows,
};
const RECEIVABLE_DRAWER_TAB_HISTORY: SegmentedTabItem<ReceivableDrawerTab> = {
  id: "history",
  label: "ประวัติ",
  icon: History,
};

const RECEIVABLE_DRAWER_PANEL_SELECTOR =
  ".record-drawer-modal-panel[data-aera-modal-panel]";

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return matches;
}

function ReceivableDismissSheet({
  isOpen,
  onClose,
  closeDisabled = false,
  subtitle,
  children,
  footer,
}: {
  isOpen: boolean;
  onClose: () => void;
  closeDisabled?: boolean;
  subtitle?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
}) {
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!isOpen) return;
    setScopedHost(document.querySelector(RECEIVABLE_DRAWER_PANEL_SELECTOR));
  }, [isDesktop, isOpen]);

  const isScoped = isDesktop && scopedHost != null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      onExited={() => setScopedHost(null)}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={closeDisabled}
      zIndexClass="z-[10050]"
      panelClassName={[
        isScoped
          ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
          : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden text-aera-charcoal"
        style={buildDataTableThemeVars()}
      >
        <div className="flex shrink-0 justify-center pb-1 pt-2">
          <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
        </div>

        <header className="flex shrink-0 items-center justify-between gap-3 px-4 pb-4">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-aera-charcoal">
              <span className="flex items-center gap-2 text-(--aera-dt-danger-text)">
                <AlertTriangle
                  className="h-5 w-5 shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
                ยืนยันการลบรายการ
              </span>
            </h2>
            {subtitle ? (
              <p className="mt-0.5 truncate text-sm text-aera-gray">{subtitle}</p>
            ) : null}
          </div>
          <ModalCloseButton
            onClose={onClose}
            disabled={closeDisabled}
            className="-mr-1 shrink-0"
          />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>

        <div className="shrink-0 space-y-3 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
          {footer}
        </div>
      </div>
    </ModalShell>
  );
}

function pickDismissSubtitleLabel(item: ReceivableListItem): string {
  if (isReceivableAddressItem(item)) {
    const nameField = item.fields.find((field) =>
      ["addr_name", "recipient_name", "customer_ref"].includes(field.key),
    );
    const name = nameField?.value?.trim();
    if (name && name !== "—") return name;
  }
  const customer = item.fields.find((field) =>
    ["customer_ref", "customer_name", "slip_sender", "sender_name"].includes(
      field.key,
    ),
  )?.value?.trim();
  if (customer && customer !== "—") return customer;
  if (item.title?.trim()) return item.title.trim();
  return item.kindLabel?.trim() || "รายการค้าง";
}

function pickDrawerImageUrl(
  item: ReceivableListItem,
  ownerUserId?: string | null,
): string | null {
  return pickReceivableSlipImageUrl(item, ownerUserId);
}

function pickDrawerAmountLabel(item: ReceivableListItem): string {
  if (isReceivableAddressItem(item)) {
    const nameField = item.fields.find((field) =>
      ["addr_name", "recipient_name", "customer_ref"].includes(field.key),
    );
    const name = nameField?.value?.trim();
    if (name && name !== "—") return name;
    if (item.title?.trim()) return item.title.trim();
    return "—";
  }
  if (item.amountLabel?.trim()) return item.amountLabel.trim();
  const amountField = item.fields.find((field) =>
    ["slip_amount", "amount", "transfer_amount", "total_amount"].includes(
      field.key,
    ),
  );
  return amountField?.value?.trim() || "—";
}

function pickDrawerSubtitle(item: ReceivableListItem): string {
  const created = item.fields.find((field) => field.key === "created_at")?.value;
  const transfer = item.fields.find(
    (field) => field.key === "transfer_date",
  )?.value;
  if (transfer?.trim()) return transfer.trim();
  if (created?.trim()) return created.trim();
  return item.title?.trim() || "—";
}

function pickDrawerExpiresAt(item: ReceivableListItem): string | null {
  const raw = item.fields.find((field) => field.key === "expires_at")?.value;
  const trimmed = raw?.trim();
  if (!trimmed || trimmed === "—") return null;
  return trimmed;
}

function ReceivableExpiryBanner({
  expiresAt,
  isDueDate,
}: {
  expiresAt: string;
  isDueDate?: boolean;
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((tick) => tick + 1), 30_000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const remaining = formatReceivableExpiryRemaining(expiresAt);
  if (!remaining || remaining === "—") return null;

  const expiresMs = Date.parse(expiresAt);
  const isExpired = remaining === "หมดอายุ";
  const isUrgent =
    Number.isFinite(expiresMs) &&
    expiresMs > Date.now() &&
    expiresMs - Date.now() < 3_600_000;

  const tone = isExpired || isUrgent
    ? {
        wrap: "border-aera-red/25 bg-aera-redlight/50 text-aera-red",
        icon: "text-aera-red",
        label: "text-aera-red/80",
        value: "text-aera-red",
      }
    : {
        wrap: "border-aera-amber/30 bg-aera-warmcream/80 text-aera-amberdark",
        icon: "text-aera-amber",
        label: "text-aera-amberdark/80",
        value: "text-aera-charcoal",
      };

  const expiredLabel = isDueDate ? "เลยครบกำหนดแล้ว" : "หมดอายุแล้ว";
  const remainingPrefix = isDueDate ? "ครบกำหนดอีก " : "หมดอายุใน ";

  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 ${tone.wrap}`}
      role="status"
    >
      <Clock className={`h-4 w-4 shrink-0 ${tone.icon}`} aria-hidden />
      <div className="min-w-0 flex-1">
        {isExpired ? (
          <p className={`text-sm font-bold leading-5 ${tone.value}`}>{expiredLabel}</p>
        ) : (
          <p className="text-sm font-bold leading-5">
            <span className={tone.label}>{remainingPrefix}</span>
            <span className={`tabular-nums ${tone.value}`}>{remaining}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function ReceivableDrawerNavButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: typeof ChevronLeft;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <AeraButton
      variant="ghost"
      size="xs"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`h-10! w-10! p-0! items-center justify-center rounded-lg transition-all ${
        disabled
          ? "cursor-not-allowed bg-transparent! text-aera-lightslate"
          : "cursor-pointer bg-transparent! text-aera-gray hover:bg-aera-lightgray/50! hover:text-aera-charcoal active:scale-[0.98]"
      }`}
    >
      <Icon className="h-4 w-4" />
    </AeraButton>
  );
}

export function ReceivableRecordDrawer({
  open,
  onOpenChange,
  item,
  slipItems = [],
  announcementItems = [],
  ownerUserId = null,
  workspaceId = null,
  onRefresh,
  onItemSynced,
  onPrev,
  onNext,
  position,
  initialTab = null,
  onTabChange,
  onMatchModalOpenChange,
  onExited,
  readOnly = false,
  canDelete = true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ReceivableListItem | null;
  slipItems?: ReceivableListItem[];
  announcementItems?: ReceivableListItem[];
  ownerUserId?: string | null;
  workspaceId?: string | null;
  onRefresh?: () => void | Promise<void>;
  onItemSynced?: (item: ReceivableListItem) => void;
  onPrev?: () => void;
  onNext?: () => void;
  position?: { current: number; total: number };
  initialTab?: ReceivableDrawerTab | null;
  onTabChange?: (tab: ReceivableDrawerTab) => void;
  onMatchModalOpenChange?: (
    open: boolean,
    kind: "match-payment" | "apply-receivable",
  ) => void;
  onExited?: () => void;
  /** Role cannot write (caller's can()) — match / create / address / resplit stay disabled. */
  readOnly?: boolean;
  /** Role may use “ลบรายการ” (caller's can() for delete). Default true. */
  canDelete?: boolean;
}) {
  const [snapshotItem, setSnapshotItem] = useState<ReceivableListItem | null>(
    null,
  );
  const [baselineFingerprint, setBaselineFingerprint] = useState<string | null>(
    null,
  );
  const [staleNotice, setStaleNotice] = useState<ReceivableStaleNotice | null>(
    null,
  );
  const [staleConflict, setStaleConflict] =
    useState<ReceivableStaleConflict | null>(null);
  const [pendingRemote, setPendingRemote] = useState<{
    remoteItem: ReceivableListItem;
    notice: ReceivableStaleNotice;
  } | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const acceptOwnRemoteUpdateRef = useRef(false);
  /** Fingerprint of the pre-apply local snapshot — ignore list lag that still shows it. */
  const ignoreListLagFingerprintRef = useRef<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [moneyMatchLists, setMoneyMatchLists] = useState<{
    announcement: ReceivableListItem[];
    slip: ReceivableListItem[];
  } | null>(null);

  const bumpHistoryRefresh = useCallback(() => {
    setHistoryRefreshKey((value) => value + 1);
  }, []);

  const noteOwnEditInThisTab = useCallback((mergeKey?: string | null) => {
    const key = (mergeKey || item?.mergeKey || snapshotItem?.mergeKey || "").trim();
    if (!key) return;
    acceptOwnRemoteUpdateRef.current = true;
    markOwnReceivableEditInThisTab(key);
    bumpHistoryRefresh();
  }, [bumpHistoryRefresh, item?.mergeKey, snapshotItem?.mergeKey]);

  const adoptRemoteQuietly = useCallback(
    (next: ReceivableListItem) => {
      setSnapshotItem(cloneReceivableListItem(next));
      setBaselineFingerprint(fingerprintReceivableItem(next));
      setStaleNotice(null);
      setStaleConflict(null);
      setPendingRemote(null);
    },
    [],
  );

  const openStaleConflict = useCallback(
    (
      local: ReceivableListItem,
      remote: ReceivableListItem,
      notice: ReceivableStaleNotice,
    ) => {
      setStaleNotice(notice);
      setStaleConflict(buildReceivableStaleConflict(local, remote, notice));
      setPendingRemote(null);
    },
    [],
  );

  useEffect(() => {
    if (!open || !item) return;
    setSnapshotItem(cloneReceivableListItem(item));
    setBaselineFingerprint(fingerprintReceivableItem(item));
    setStaleNotice(null);
    setStaleConflict(null);
    setPendingRemote(null);
    setIsReloading(false);
    acceptOwnRemoteUpdateRef.current = false;
    ignoreListLagFingerprintRef.current = null;
  }, [open, item?.mergeKey]);

  useEffect(() => {
    if (!open || !item || !baselineFingerprint || staleConflict || pendingRemote) {
      return;
    }
    if (isReloading) return;

    const itemFingerprint = fingerprintReceivableItem(item);
    if (
      ignoreListLagFingerprintRef.current &&
      itemFingerprint === ignoreListLagFingerprintRef.current
    ) {
      // List still serving the pre-apply local row — wait for refetch to catch up.
      return;
    }
    if (itemFingerprint === baselineFingerprint) {
      ignoreListLagFingerprintRef.current = null;
    }

    const owner = ownerUserId?.trim() || resolveOwnerUserId();
    const detected = detectStaleReceivableEdit(baselineFingerprint, item, owner);
    if (!detected || !snapshotItem) return;

    // Same browser tab that just edited: adopt quietly (other tabs still get the banner).
    // Only run when fingerprint actually changed — otherwise setState loops forever while
    // wasOwnReceivableEditInThisTab stays true for ~30s.
    if (
      acceptOwnRemoteUpdateRef.current ||
      wasOwnReceivableEditInThisTab(item.mergeKey)
    ) {
      acceptOwnRemoteUpdateRef.current = false;
      ignoreListLagFingerprintRef.current = null;
      adoptRemoteQuietly(item);
      return;
    }

    let cancelled = false;
    void (async () => {
      let notice = detected;
      if (owner) {
        try {
          const rows = await fetchPendingItemHistory(item, owner);
          if (cancelled) return;
          const latest = rows.find((row) => row.isLatest) ?? rows[0] ?? null;
          notice = enrichStaleNoticeFromHistory(detected, latest, owner);
        } catch {
          // Keep fingerprint-based notice if history is unavailable.
        }
      }
      if (cancelled) return;

      // Re-check after await — this tab may have finished its own edit meanwhile.
      if (wasOwnReceivableEditInThisTab(item.mergeKey)) {
        ignoreListLagFingerprintRef.current = null;
        adoptRemoteQuietly(item);
        return;
      }

      openStaleConflict(cloneReceivableListItem(snapshotItem), item, notice);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    adoptRemoteQuietly,
    baselineFingerprint,
    isReloading,
    item,
    open,
    openStaleConflict,
    ownerUserId,
    pendingRemote,
    snapshotItem,
    staleConflict,
  ]);

  const activeItem = snapshotItem;

  const [activeTab, setActiveTab] = useState<ReceivableDrawerTab>("details");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [createReceivableOpen, setCreateReceivableOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissBusy, setDismissBusy] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [addressDraft, setAddressDraft] = useState<PendingAddressDraft | null>(
    null,
  );
  const [addressSaveBusy, setAddressSaveBusy] = useState(false);
  const [addressSaveError, setAddressSaveError] = useState<string | null>(null);
  const [resplitAllocations, setResplitAllocations] = useState<
    Array<{
      allocation_id: string;
      receivable_id: string;
      amount: string;
      label?: string;
    }>
  >([]);
  const [resplitLoading, setResplitLoading] = useState(false);

  const reloadResplitAllocations = useCallback(async () => {
    const paymentId = activeItem?.paymentId?.trim();
    const owner = ownerUserId?.trim();
    if (!paymentId || !owner) return;
    try {
      const data = await fetchPaymentAllocations(paymentId, owner);
      setResplitAllocations(
        (data.allocations ?? []).map((a) => ({
          allocation_id: a.allocation_id,
          receivable_id: a.receivable_id,
          amount: a.amount || "0",
          label: a.customer_name?.trim() || undefined,
        })),
      );
    } catch {
      // Keep last known allocations if refresh fails.
    }
  }, [activeItem?.paymentId, ownerUserId]);

  const showCorrectionsTab = Boolean(
    activeItem && canUnmatchReceivable(activeItem),
  );
  const showResplitTab = Boolean(
    !readOnly &&
      activeItem &&
      isReceivableSlipItem(activeItem) &&
      activeItem.paymentId?.trim() &&
      !isReceivableArchivedDisplayStatus(activeItem),
  );
  const canDismissPending = Boolean(
    activeItem && canDismissPendingItem(activeItem),
  );
  const showDismissAction = Boolean(
    canDelete && activeItem && isDismissPendingActionVisible(activeItem),
  );
  const actionsLocked = Boolean(
    readOnly || (activeItem && isReceivableArchivedDisplayStatus(activeItem)),
  );
  const dismissBlockReason = activeItem
    ? staleConflict
      ? "มีคนอื่นแก้รายการนี้แล้ว — รีเฟรชก่อนแล้วค่อยลองใหม่"
      : getDismissPendingBlockReason(activeItem)
    : "ไม่พบรายการ";
  const dismissDisabled = Boolean(
    !canDismissPending || staleConflict || dismissBusy || actionsLocked,
  );
  const canEditAddress = Boolean(
    !readOnly && activeItem && canEditPendingAddress(activeItem),
  );
  const resolvedAddressDraft =
    addressDraft ??
    (activeItem && canEditAddress
      ? pickPendingAddressDraft(activeItem)
      : null);
  const addressDirty = Boolean(
    activeItem &&
      resolvedAddressDraft &&
      isPendingAddressDraftDirty(activeItem, resolvedAddressDraft),
  );
  const addressCanSave = Boolean(
    canEditAddress &&
      resolvedAddressDraft &&
      addressDirty &&
      isPendingAddressDraftValid(resolvedAddressDraft) &&
      !addressSaveBusy &&
      !staleConflict,
  );

  useEffect(() => {
    if (!open || !activeItem || !canEditPendingAddress(activeItem)) {
      setAddressDraft(null);
      setAddressSaveError(null);
      setAddressSaveBusy(false);
      return;
    }
    setAddressDraft(pickPendingAddressDraft(activeItem));
    setAddressSaveError(null);
    setAddressSaveBusy(false);
  }, [open, activeItem?.mergeKey, canEditAddress]);

  const isValidDrawerTab = useCallback(
    (tab: string | null | undefined): tab is ReceivableDrawerTab => {
      if (tab === "details" || tab === "history") return true;
      if (tab === "corrections") return showCorrectionsTab;
      if (tab === "resplit") return showResplitTab;
      return false;
    },
    [showCorrectionsTab, showResplitTab],
  );

  useEffect(() => {
    if (isValidDrawerTab(initialTab)) setActiveTab(initialTab);
    else setActiveTab("details");
    setMatchModalOpen(false);
    setCreateReceivableOpen(false);
    setDismissOpen(false);
    setDismissBusy(false);
    setDismissError(null);
  }, [activeItem?.mergeKey, initialTab, isValidDrawerTab]);

  useEffect(() => {
    if (!open) return;
    if (isValidDrawerTab(initialTab)) setActiveTab(initialTab);
  }, [initialTab, isValidDrawerTab, open]);

  useEffect(() => {
    if (activeTab === "corrections" && !showCorrectionsTab) {
      setActiveTab("details");
    }
    if (activeTab === "resplit" && !showResplitTab) {
      setActiveTab("details");
    }
  }, [activeTab, showCorrectionsTab, showResplitTab]);

  const applyRemoteItem = useCallback(
    async (remote: ReceivableListItem) => {
      setIsReloading(true);
      try {
        // Remember the pre-apply local row so a lagging list refetch cannot reopen
        // the stale banner with the old values as "remote".
        if (snapshotItem) {
          ignoreListLagFingerprintRef.current =
            fingerprintReceivableItem(snapshotItem);
        }
        // Apply the known-good remote snapshot immediately so the drawer matches the
        // banner's "ข้อมูลใหม่" even if list refresh is slow or returns a racey `item`.
        const next = cloneReceivableListItem(remote);
        setSnapshotItem(next);
        setBaselineFingerprint(fingerprintReceivableItem(next));
        setStaleNotice(null);
        setStaleConflict(null);
        setPendingRemote(null);
        onItemSynced?.(next);
        try {
          await onRefresh?.();
        } catch {
          // Keep the applied remote view; list can catch up on the next poll.
        }
      } finally {
        setIsReloading(false);
      }
    },
    [onItemSynced, onRefresh, snapshotItem],
  );

  const handleUseRemote = useCallback(() => {
    if (!staleConflict) return;
    void applyRemoteItem(staleConflict.remoteItem);
  }, [applyRemoteItem, staleConflict]);

  const handleApplyPendingRemote = useCallback(() => {
    if (!pendingRemote) return;
    void applyRemoteItem(pendingRemote.remoteItem);
  }, [applyRemoteItem, pendingRemote]);

  const handleModalExited = useCallback(() => {
    setSnapshotItem(null);
    setBaselineFingerprint(null);
    setStaleNotice(null);
    setStaleConflict(null);
    setPendingRemote(null);
    setIsReloading(false);
    acceptOwnRemoteUpdateRef.current = false;
    ignoreListLagFingerprintRef.current = null;
    setActiveTab("details");
    setViewerOpen(false);
    setImageLoadFailed(false);
    setDismissOpen(false);
    setDismissBusy(false);
    setDismissError(null);
    onExited?.();
  }, [onExited]);

  const imageUrl = useMemo(
    () => (activeItem ? pickDrawerImageUrl(activeItem, ownerUserId) : null),
    [activeItem, ownerUserId],
  );

  useEffect(() => {
    setImageLoadFailed(false);
    setViewerOpen(false);
  }, [activeItem?.mergeKey, imageUrl]);

  useEffect(() => {
    if (!open) return;

    const handleKey = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onPrev?.();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        onNext?.();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onNext, onOpenChange, onPrev, open]);

  const [historyCount, setHistoryCount] = useState(0);
  const [historyCountLoading, setHistoryCountLoading] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [matchCountLoading, setMatchCountLoading] = useState(false);
  const [latestMeta, setLatestMeta] = useState<{
    label: string;
    actorName: string;
    occurredAt: string;
  } | null>(null);

  useEffect(() => {
    if (!open || !activeItem) {
      setHistoryCount(0);
      setHistoryCountLoading(false);
      setLatestMeta(null);
      return;
    }
    const owner = ownerUserId?.trim() || resolveOwnerUserId();
    if (!owner) {
      setHistoryCount(0);
      setHistoryCountLoading(false);
      setLatestMeta(null);
      return;
    }
    let cancelled = false;
    setHistoryCount(0);
    setHistoryCountLoading(true);
    void fetchPendingItemHistory(activeItem, owner)
      .then((entries) => {
        if (cancelled) return;
        setHistoryCount(entries.length);
        setLatestMeta(pickLatestHistoryMeta(entries));
      })
      .catch(() => {
        if (cancelled) return;
        setHistoryCount(0);
        setLatestMeta(null);
      })
      .finally(() => {
        if (!cancelled) setHistoryCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeItem?.mergeKey, historyRefreshKey, open, ownerUserId]);

  useEffect(() => {
    if (!open || activeTab !== "history") return;
    bumpHistoryRefresh();
  }, [activeTab, bumpHistoryRefresh, open]);

  useEffect(() => {
    if (!open || !activeItem || !showCorrectionsTab) {
      setMatchCount(0);
      setMatchCountLoading(false);
      return;
    }
    const owner = ownerUserId?.trim() || resolveOwnerUserId();
    const receivableId = pickReceivableId(activeItem);
    if (!owner || !receivableId) {
      setMatchCount(0);
      setMatchCountLoading(false);
      return;
    }
    let cancelled = false;
    setMatchCount(0);
    setMatchCountLoading(true);
    void fetchUnmatchCandidates(receivableId, owner)
      .then((candidates) => {
        if (!cancelled) setMatchCount(candidates.length);
      })
      .catch(() => {
        if (!cancelled) setMatchCount(0);
      })
      .finally(() => {
        if (!cancelled) setMatchCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    activeItem?.mergeKey,
    activeTab,
    historyRefreshKey,
    open,
    ownerUserId,
    showCorrectionsTab,
  ]);

  const handleMatchCountChange = useCallback((count: number) => {
    setMatchCount(count);
  }, []);

  useEffect(() => {
    if (!open || !showResplitTab || !activeItem) {
      setResplitAllocations([]);
      setResplitLoading(false);
      return;
    }
    const paymentId = activeItem.paymentId?.trim();
    const owner = ownerUserId?.trim();
    if (!paymentId || !owner) {
      setResplitAllocations([]);
      setResplitLoading(false);
      return;
    }

    let cancelled = false;
    setResplitLoading(true);
    void (async () => {
      try {
        // Payment-centric: include allocations to receivables that left the
        // hot queue (fully paid). Scanning announcementItems alone misses them.
        const data = await fetchPaymentAllocations(paymentId, owner);
        if (cancelled) return;
        setResplitAllocations(
          (data.allocations ?? []).map((a) => ({
            allocation_id: a.allocation_id,
            receivable_id: a.receivable_id,
            amount: a.amount || "0",
            label: a.customer_name?.trim() || undefined,
          })),
        );
      } catch {
        if (cancelled) return;
        setResplitAllocations([]);
      } finally {
        if (!cancelled) setResplitLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Prefetch while drawer is open so the tab badge has a count before visit.
  }, [activeItem?.mergeKey, activeItem?.paymentId, open, ownerUserId, showResplitTab]);

  useEffect(() => {
    if (!open) {
      setMoneyMatchLists(null);
      return;
    }
    const owner = ownerUserId?.trim() || "";
    const ws = (workspaceId || "").trim();
    if (!owner || !ws) return;
    let cancelled = false;
    // Unscoped pending list: ตัดยอดค้าง must not inherit สลิปค้าง kind/date/q filters.
    void fetchReceivableDebtsDetails(owner, { workspaceId: ws })
      .then((details) => {
        if (cancelled) return;
        setMoneyMatchLists({
          announcement: filterReceivableMoneyCandidates(
            details.lists.byKind.announcement ?? [],
          ),
          slip: filterReceivableMoneyCandidates(details.lists.byKind.slip ?? []),
        });
      })
      .catch(() => {
        /* keep tab-scoped announcementItems / slipItems */
      });
    return () => {
      cancelled = true;
    };
  }, [open, ownerUserId, workspaceId]);

  const resplitCount = resplitAllocations.length;
  const matchAnnouncementItems = moneyMatchLists?.announcement ?? announcementItems;
  const matchSlipItems = moneyMatchLists?.slip ?? slipItems;
  const applyReceivableCount = useMemo(
    () => filterReceivableMoneyCandidates(matchAnnouncementItems).length,
    [matchAnnouncementItems],
  );

  const drawerTabs = useMemo((): SegmentedTabItem<ReceivableDrawerTab>[] => {
    const tabs: SegmentedTabItem<ReceivableDrawerTab>[] = [
      RECEIVABLE_DRAWER_TAB_DETAILS,
    ];
    if (showResplitTab) {
      tabs.push({
        ...RECEIVABLE_DRAWER_TAB_RESPLIT,
        count: resplitCount > 0 ? resplitCount : undefined,
        countLoading: resplitLoading,
      });
    }
    if (showCorrectionsTab) {
      tabs.push({
        ...RECEIVABLE_DRAWER_TAB_CORRECTIONS,
        count: matchCount > 0 ? matchCount : undefined,
        countLoading: matchCountLoading,
      });
    }
    tabs.push({
      ...RECEIVABLE_DRAWER_TAB_HISTORY,
      count: historyCount > 0 ? historyCount : undefined,
      countLoading: historyCountLoading,
    });
    return tabs;
  }, [
    historyCount,
    historyCountLoading,
    matchCount,
    matchCountLoading,
    resplitCount,
    resplitLoading,
    showCorrectionsTab,
    showResplitTab,
  ]);

  const handleDraftChange = useCallback(
    (next: ReceivableListItem) => {
      setSnapshotItem(cloneReceivableListItem(next));
      onItemSynced?.(next);
    },
    [onItemSynced],
  );

  if (!activeItem) return null;

  const amountLabel = pickDrawerAmountLabel(activeItem);
  const subtitle = pickDrawerSubtitle(activeItem);
  const expiresAt = pickDrawerExpiresAt(activeItem);
  const kindLabel = activeItem.kindLabel?.trim() || "รายการค้าง";
  const staleEntityLabel = isReceivableSlipItem(activeItem)
    ? "สลิปค้าง"
    : isReceivableAnnouncementItem(activeItem)
      ? "ยอดค้าง"
      : kindLabel;
  const dismissSubtitleLabel = pickDismissSubtitleLabel(activeItem);
  const dismissSubtitleAmount =
    activeItem.amountLabel?.trim() ||
    (!isReceivableAddressItem(activeItem) ? amountLabel : null);
  const canMatchPayment = isReceivableAnnouncementItem(activeItem);
  const canApplyReceivable = isReceivableSlipItem(activeItem);
  const actionsDisabled = Boolean(staleConflict) || actionsLocked;
  const matchModalMode: ReceivableMatchModalMode = canApplyReceivable
    ? "from-slip"
    : "from-receivable";
  const matchCounterpartItems = canApplyReceivable
    ? matchAnnouncementItems
    : matchSlipItems;

  const drawerNav = (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <ReceivableDrawerNavButton
          icon={ChevronLeft}
          label="ก่อนหน้า"
          onClick={onPrev}
          disabled={!onPrev || Boolean(staleConflict)}
        />
        <ReceivableDrawerNavButton
          icon={ChevronRight}
          label="ถัดไป"
          onClick={onNext}
          disabled={!onNext || Boolean(staleConflict)}
        />
        {position ? (
          <div className="ml-1 px-1 py-1 text-[11px] font-bold tabular-nums tracking-wide text-aera-gray">
            {position.current} / {position.total}
          </div>
        ) : null}
      </div>
      <ModalCloseButton onClose={() => onOpenChange(false)} className="-mr-1" />
    </div>
  );

  const themedContent = (
    <div
      className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden text-aera-charcoal"
      style={buildDataTableThemeVars()}
    >
      {staleConflict ? (
        <ReceivableStaleBanner
          conflict={staleConflict}
          entityLabel={staleEntityLabel}
          isReloading={isReloading}
          onUseRemote={handleUseRemote}
        />
      ) : null}

      <div
        className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
          staleConflict ? "pointer-events-none opacity-55" : ""
        }`}
      >
      <div className="shrink-0 border-b border-aera-lightgray/80 pb-4 mb-4 px-0">
        <div className="space-y-2">
          {pendingRemote ? (
            <ReceivableViewingStaleChip
              actorName={pendingRemote.notice.actorName}
              entityLabel={staleEntityLabel}
              onUseRemote={handleApplyPendingRemote}
            />
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-3xl font-bold tracking-tight text-aera-charcoal">
              {amountLabel}
            </h2>
            <StatusBadge
              status={resolveReceivableDisplayStatus(activeItem)}
              className="px-3 py-1 text-xs font-bold shadow-none"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-aera-lightslate">
            <span className="text-[11px] tracking-tight text-aera-gray">
              {kindLabel}
            </span>
            <span className="text-aera-lightslate/60">•</span>
            <span>{subtitle}</span>
          </div>
          {expiresAt ? (
            <ReceivableExpiryBanner
              expiresAt={expiresAt}
              isDueDate={isReceivableAnnouncementItem(activeItem)}
            />
          ) : null}
          {latestMeta && activeTab === "details" ? (
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className="cursor-pointer text-left text-xs font-medium text-aera-gray transition-colors hover:text-aera-charcoal"
            >
              ล่าสุด:{" "}
              <span className="font-semibold text-aera-charcoal">
                {latestMeta.label}
              </span>
              <span className="mx-1.5 text-aera-lightslate/70">·</span>
              <span className="tabular-nums">{latestMeta.occurredAt}</span>
              <span className="ml-1 text-aera-amber">ดูประวัติ →</span>
            </button>
          ) : null}
        </div>

        <div className="mt-4 border-t border-aera-lightgray/80 pt-3 [&_button]:cursor-pointer [&_button:disabled]:cursor-not-allowed">
          <SegmentedTabSwitcher
            tabs={drawerTabs}
            activeTab={activeTab}
            onChange={(tab) => {
              setActiveTab(tab);
              onTabChange?.(tab);
            }}
            ariaLabel="เมนูยอดค้าง"
          />
        </div>
      </div>

      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
        <div
          key={activeTab}
          className={`space-y-4 animate-in fade-in duration-300 fill-mode-both ${
            activeTab === "history" ||
            activeTab === "corrections" ||
            activeTab === "resplit"
              ? "slide-in-from-right-2"
              : "slide-in-from-left-2"
          }`}
        >
        {activeTab === "history" ? (
          <ReceivableHistoryTab
            key={activeItem.mergeKey}
            item={activeItem}
            ownerUserId={ownerUserId}
            workspaceId={workspaceId}
            refreshKey={historyRefreshKey}
            readOnly={readOnly}
            onUndone={async () => {
              noteOwnEditInThisTab(activeItem.mergeKey);
              await onRefresh?.();
            }}
          />
        ) : activeTab === "corrections" ? (
          <ReceivableCorrectionsPanel
            item={activeItem}
            ownerUserId={ownerUserId}
            workspaceId={workspaceId}
            destinationCandidates={matchAnnouncementItems}
            disabled={actionsDisabled}
            onMatchCountChange={handleMatchCountChange}
            onApplied={async () => {
              noteOwnEditInThisTab(activeItem.mergeKey);
              bumpHistoryRefresh();
              await onRefresh?.();
            }}
          />
        ) : activeTab === "resplit" && activeItem.paymentId ? (
          resplitLoading ? (
            <ResplitAllocationSkeleton />
          ) : (
            <ResplitAllocationModal
              embedded
              open
              onOpenChange={(next) => {
                if (!next) setActiveTab("details");
              }}
              paymentId={activeItem.paymentId}
              paymentRemainingLabel={
                activeItem.fields.find((f) => f.key === "amount_remaining")
                  ?.value ||
                activeItem.amountLabel ||
                amountLabel
              }
              currentAllocations={resplitAllocations}
              receivableCandidates={matchAnnouncementItems}
              ownerUserId={ownerUserId}
              workspaceId={workspaceId}
              onResplit={async (info) => {
                if (ownerUserId && workspaceId) {
                  offerMoneyUndoToast({
                    message:
                      info.label ||
                      (info.cleared
                        ? "ยกเลิกจัดสรรทั้งหมด"
                        : pickMoneyUndoSourceLabel(activeItem) ||
                          "บันทึกยอดจัดสรรแล้ว"),
                    ownerUserId,
                    workspaceId,
                    operationId: info.operationId,
                    onUndone: onRefresh,
                  });
                }
                noteOwnEditInThisTab(activeItem.mergeKey);
                await reloadResplitAllocations();
                await onRefresh?.();
              }}
            />
          )
        ) : (
          <ReceivableDetailsTab
            key={activeItem.mergeKey}
            item={activeItem}
            disabled={actionsDisabled}
            imageUrl={imageUrl}
            kindLabel={kindLabel}
            amountLabel={amountLabel}
            viewerOpen={viewerOpen}
            imageLoadFailed={imageLoadFailed}
            onViewerOpenChange={setViewerOpen}
            onImageError={() => setImageLoadFailed(true)}
            onDraftChange={handleDraftChange}
            ownerUserId={ownerUserId}
            workspaceId={workspaceId}
            onAmountApplied={async () => {
              noteOwnEditInThisTab(activeItem.mergeKey);
              await onRefresh?.();
            }}
            addressDraft={canEditAddress ? resolvedAddressDraft : null}
            onAddressDraftChange={setAddressDraft}
            addressSaveBusy={addressSaveBusy}
          />
        )}
        </div>
      </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={open}
        onClose={() => onOpenChange(false)}
        onExited={handleModalExited}
        header={<ModalHeader>{drawerNav}</ModalHeader>}
        footer={
          <div className="w-full">
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {canEditAddress && addressDirty ? (
                  <AeraButton
                    type="button"
                    variant="primary"
                    fontWeight="medium"
                    className="cursor-pointer"
                    loading={addressSaveBusy}
                    disabled={!addressCanSave}
                    onClick={() => {
                      void (async () => {
                        if (!activeItem || !resolvedAddressDraft || !addressCanSave)
                          return;
                        setAddressSaveBusy(true);
                        setAddressSaveError(null);
                        try {
                          const result = await savePendingAddressDraft({
                            item: activeItem,
                            draft: resolvedAddressDraft,
                            ownerUserId,
                            workspaceId,
                          });
                          showToast(result.summary || "บันทึกที่อยู่แล้ว");
                          noteOwnEditInThisTab(activeItem.mergeKey);
                          await onRefresh?.();
                        } catch (err) {
                          const message =
                            err instanceof Error
                              ? err.message
                              : "แก้ไขที่อยู่ไม่สำเร็จ";
                          setAddressSaveError(message);
                          showToast(message, "error");
                        } finally {
                          setAddressSaveBusy(false);
                        }
                      })();
                    }}
                  >
                    {addressSaveBusy ? "กำลังบันทึก…" : "บันทึกที่อยู่"}
                  </AeraButton>
                ) : null}
                {canMatchPayment ? (
                  <AeraButton
                    type="button"
                    variant="primary"
                    fontWeight="medium"
                    className="cursor-pointer"
                    disabled={actionsDisabled}
                    onClick={() => {
                      setMatchModalOpen(true);
                      onMatchModalOpenChange?.(true, "match-payment");
                    }}
                  >
                    จับคู่การชำระเงิน
                  </AeraButton>
                ) : null}
                {canApplyReceivable ? (
                  <>
                    <AeraButton
                      type="button"
                      variant="primary"
                      fontWeight="medium"
                      className="cursor-pointer"
                      disabled={actionsDisabled}
                      onClick={() => {
                        setMatchModalOpen(true);
                        onMatchModalOpenChange?.(true, "apply-receivable");
                      }}
                    >
                      {applyReceivableCount > 0
                        ? `ตัดยอดค้าง (${applyReceivableCount.toLocaleString("th-TH")})`
                        : "ตัดยอดค้าง"}
                    </AeraButton>
                    <AeraButton
                      type="button"
                      variant="secondary"
                      fontWeight="medium"
                      className="cursor-pointer"
                      disabled={actionsDisabled}
                      onClick={() => setCreateReceivableOpen(true)}
                    >
                      สร้างยอดค้างใหม่
                    </AeraButton>
                  </>
                ) : null}
                {!canMatchPayment && !canApplyReceivable && !addressDirty ? (
                  <span />
                ) : null}
                {showDismissAction ? (
                  dismissDisabled && dismissBlockReason ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-not-allowed">
                            <AeraButton
                              type="button"
                              variant="danger"
                              fontWeight="medium"
                              className="pointer-events-none"
                              disabled
                            >
                              ลบรายการ
                            </AeraButton>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-[16rem] whitespace-normal wrap-break-word bg-aera-white text-left leading-relaxed"
                        >
                          {dismissBlockReason}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <AeraButton
                      type="button"
                      variant="danger"
                      fontWeight="medium"
                      className="cursor-pointer"
                      disabled={dismissBusy}
                      onClick={() => {
                        setDismissError(null);
                        setDismissOpen(true);
                      }}
                    >
                      ลบรายการ
                    </AeraButton>
                  )
                ) : null}
              </div>
              <AeraButton
                type="button"
                variant="outline"
                fontWeight="medium"
                className="cursor-pointer"
                onClick={() => onOpenChange(false)}
              >
                ปิด
              </AeraButton>
            </div>
            {addressSaveError ? (
              <p className="mt-2 w-full text-xs font-medium text-aera-red">
                {addressSaveError}
              </p>
            ) : null}
          </div>
        }
        size="2xl"
        placement="responsive-drawer"
        sheetHost
        panelClassName="record-drawer-modal-panel"
        bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0 [&>div]:flex [&>div]:h-full [&>div]:min-h-0 [&>div]:flex-1 [&>div]:flex-col"
        zIndexClass="z-9998"
      >
        {themedContent}
        <ReceivableDismissSheet
          isOpen={dismissOpen}
          onClose={() => {
            if (dismissBusy) return;
            setDismissOpen(false);
          }}
          closeDisabled={dismissBusy}
          subtitle={
            dismissSubtitleAmount &&
            dismissSubtitleAmount !== dismissSubtitleLabel ? (
              <>
                {dismissSubtitleLabel}{" "}
                <span className="text-aera-gray">•</span>{" "}
                {dismissSubtitleAmount}
              </>
            ) : (
              dismissSubtitleLabel
            )
          }
          footer={
            <>
              <AeraButton
                type="button"
                variant="danger"
                fontWeight="medium"
                fullWidth
                className="cursor-pointer"
                loading={dismissBusy}
                disabled={dismissBusy}
                onClick={() => {
                  void (async () => {
                    const owner = ownerUserId?.trim() || resolveOwnerUserId();
                    const orderId = activeItem
                      ? getReceivableOrderId(activeItem)
                      : null;
                    if (!owner || !orderId) {
                      setDismissError("ไม่พบรายการในคิว");
                      return;
                    }
                    setDismissBusy(true);
                    setDismissError(null);
                    try {
                      const result = await postDismissPending({
                        ownerUserId: owner,
                        workspaceId: workspaceId ?? undefined,
                        orderId,
                      });
                      showToast(
                        result.summary_lines?.[0] || "ลบรายการแล้ว",
                      );
                      setDismissOpen(false);
                      await onRefresh?.();
                      onOpenChange(false);
                    } catch (err) {
                      setDismissError(
                        err instanceof Error
                          ? err.message
                          : "ลบรายการไม่สำเร็จ",
                      );
                    } finally {
                      setDismissBusy(false);
                    }
                  })();
                }}
              >
                {dismissBusy ? "กำลังลบ…" : "ยืนยันลบรายการ"}
              </AeraButton>
              <AeraButton
                type="button"
                variant="outline"
                fontWeight="medium"
                fullWidth
                className="cursor-pointer"
                disabled={dismissBusy}
                onClick={() => setDismissOpen(false)}
              >
                ปิด
              </AeraButton>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-aera-gray">
              การลบจะเปลี่ยนสถานะเป็น{" "}
              <span className="font-bold text-(--aera-dt-danger-text)">
                ลบรายการ
              </span>{" "}
              และไม่สามารถย้อนกลับได้
            </p>
            <p className="text-sm leading-relaxed text-aera-gray">
              {isReceivableAddressItem(activeItem)
                ? "รายการจะถูกเอาออกจากคิวที่อยู่ค้าง ข้อมูลที่อยู่ยังเก็บไว้ในระบบ และยังดูย้อนหลังได้"
                : "รายการจะถูกเอาออกจากคิวค้าง ยอดเงินยังอยู่ในระบบ ไม่ได้หายไป และยังดูย้อนหลังได้"}
            </p>
            {dismissError ? (
              <p className="text-xs font-medium text-aera-red">{dismissError}</p>
            ) : null}
          </div>
        </ReceivableDismissSheet>
      </Modal>

      <ReceivableMatchPaymentModal
        open={matchModalOpen}
        onOpenChange={(next) => {
          setMatchModalOpen(next);
          // If called from internal modal close (e.g. backdrop / X), infer kind from anchor.
          const kind = isReceivableSlipItem(activeItem)
            ? "apply-receivable"
            : "match-payment";
          onMatchModalOpenChange?.(next, kind);
        }}
        mode={matchModalMode}
        anchorItem={activeItem}
        counterpartItems={matchCounterpartItems}
        ownerUserId={ownerUserId}
        workspaceId={workspaceId}
        onMatched={async () => {
          noteOwnEditInThisTab(activeItem.mergeKey);
          await onRefresh?.();
          onOpenChange(false);
        }}
      />

      {activeItem ? (
        <CreateReceivableFromSlipModal
          open={createReceivableOpen}
          onOpenChange={setCreateReceivableOpen}
          slipItem={activeItem}
          ownerUserId={ownerUserId}
          workspaceId={workspaceId}
          onCreated={async () => {
            noteOwnEditInThisTab(activeItem.mergeKey);
            await onRefresh?.();
            onOpenChange(false);
          }}
        />
      ) : null}
    </>
  );
}
